import { spineLoader } from '@play-co/timestep-spine';
import type { ThemeAsset } from 'src/game/theme/Theme';
import type { DreamDriftNodeStatus } from '../components/DreamDriftNode';
import type {
  DreamDriftHomePetAssets,
  DreamDriftHudButtonAssets,
  DreamDriftMapConfig,
  DreamDriftThemeProvider,
} from '../dreamDrift.theme';

/** Root folder containing the Urashima presentation assets. */
const themeFolder = 'assets/ui/dreamDrift/theme-urashima';

/** Folder containing Urashima's map slices and progression plates. */
const mapFolder = `${themeFolder}/map`;

/** Animated HUD icon and timer panel reused throughout an Urashima event. */
const hudButtonAssets: DreamDriftHudButtonAssets = {
  levelLabelLayout: { x: 116, y: 35, r: 0 },
  kind: 'spine',
  spine: {
    loadMethod: spineLoader,
    url: `${themeFolder}/hudButton/spine/urashima.HUDicon`,
  },
  timer: `${themeFolder}/hudButton/timer.png`,
};

/** Bed shown beneath the sleeping pet during an Urashima run. */
const homePetAssets: DreamDriftHomePetAssets = {
  bed: `${themeFolder}/bed.png`,
};

/** Leaf artwork associated with each regular node status. */
const NODE_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'node_cleared.png',
  current: 'node_current.png',
  open: 'node_open.png',
};

/** Checkpoint artwork shared by current and upcoming milestones. */
const CHECKPOINT_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'checkpoint_cleared.png',
  current: 'checkpoint_current.png',
  open: 'checkpoint_current.png',
};

/** Animated underwater map and the ten- and twelve-node river paths. */
const mapConfig = {
  dimensions: { width: 1080, height: 3452 },
  numSlices: 4,
  order: ['bg01.png', 'bg02.png', 'bg03.png', 'bg04.png'],
  chests: [
    { image: 'chest_bronze.png', x: 745, y: 2060, zIndex: 8 },
    { image: 'chest_silver.png', x: 650, y: 1340, zIndex: 5 },
    // Set the new gold chest back from the final leaf, beside the shipwreck.
    { image: 'chest_gold.png', x: 385, y: 400, zIndex: 0 },
  ],
  chests12: [
    { image: 'chest_bronze.png', x: 760, y: 2070, zIndex: 10 },
    { image: 'chest_silver.png', x: 650, y: 1340, zIndex: 6 },
    // Match the gold chest placement on the shared summit island.
    { image: 'chest_gold.png', x: 385, y: 400, zIndex: 0 },
  ],
  spine: {
    path: `${mapFolder}/spine/dreamdrift.map2`,
    animations: [
      { id: 'urashima_idle', x: 220, y: 2790, scale: 0.95 },
      { id: 'redfish_idle', x: 184, y: 1840, scale: 1 },
      { id: 'flounder_idle', x: 726, y: 2015, scale: 1 },
      { id: 'ohime_idle', x: 317, y: 1295, scale: 0.95 },
    ],
  },
  nodes: [
    { x: 520, y: 2930 },
    { x: 570, y: 2770 },
    { x: 450, y: 2595 },
    { x: 590, y: 2120 },
    { x: 350, y: 2000 },
    { x: 630, y: 1810 },
    { x: 500, y: 1380 },
    { x: 705, y: 1240 },
    { x: 655, y: 1040 },
    { x: 680, y: 610 },
  ],
  nodes12: [
    { x: 520, y: 2930 },
    { x: 570, y: 2770 },
    { x: 450, y: 2595 },
    { x: 600, y: 2140 },
    { x: 360, y: 2040 },
    { x: 480, y: 1920 },
    { x: 580, y: 1795 },
    { x: 500, y: 1405 },
    { x: 745, y: 1270 },
    { x: 650, y: 1135 },
    { x: 635, y: 990 },
    { x: 680, y: 610 },
  ],
} satisfies DreamDriftMapConfig;

/** Get every asset required to display the Urashima map and home pet. */
function getAssets(): ThemeAsset[] {
  // Preload the map artwork and shared Spine export before the popup opens.
  return [
    ...mapConfig.order.map(getMapImagePath),
    ...mapConfig.chests.map(({ image }) => getMapImagePath(image)),
    ...Object.values(NODE_IMAGES).map(getMapImagePath),
    ...[...new Set(Object.values(CHECKPOINT_IMAGES))].map(getMapImagePath),
    getMapImagePath('checkpoint_flag.png'),
    { url: mapConfig.spine.path, loadMethod: spineLoader },
    ...Object.values(homePetAssets),
  ];
}

/** Get the Urashima bed artwork used by the active home-pet presentation. */
function getHomePetAssets(): DreamDriftHomePetAssets {
  // Return the stable asset description for the persistent home view.
  return homePetAssets;
}

/** Get the animated Urashima HUD icon and its timer panel. */
function getHudButtonAssets(): DreamDriftHudButtonAssets {
  // Return the stable asset description so HUD updates can detect theme changes.
  return hudButtonAssets;
}

/** Get the square illustration displayed in the Urashima introduction. */
function getIntroImagePath(): string {
  // Resolve the introduction inside this theme's asset directory.
  return `${themeFolder}/intro.png`;
}

/** Get the underwater background displayed behind Urashima puzzles. */
function getPuzzleBackground(): string {
  // Keep puzzle art aligned with the event's map and home presentation.
  return 'assets/minigame/dreamDrift/theme-urashima/bg.png';
}

/** Get the source dimensions and both Urashima progression paths. */
function getMapConfig(): DreamDriftMapConfig {
  // Return the map description consumed by the shared map component.
  return mapConfig;
}

/** Resolve a map image filename to its full Urashima asset path. */
function getMapImagePath(image: string): string {
  // Resolve plates, chests, and background slices within the map directory.
  return `${mapFolder}/${image}`;
}

/** Get the regular or checkpoint leaf image for a progression status. */
function getNodeImage({
  status,
  isCheckpoint,
}: Parameters<DreamDriftThemeProvider['getNodeImage']>[0]): string {
  // Select the matching plate before resolving its full path.
  const images = isCheckpoint ? CHECKPOINT_IMAGES : NODE_IMAGES;
  return getMapImagePath(images[status]);
}

/**
 * Get the wide leaf plate layout and upright white numbering.
 * @param isCheckpoint Whether the leaf marks a reward checkpoint.
 * @returns Plate bounds, number styling, and flag placement in source pixels.
 */
function getNodeStyle(
  isCheckpoint: boolean,
): ReturnType<DreamDriftThemeProvider['getNodeStyle']> {
  // Match each leaf's aspect ratio instead of using Momotaro's tall tree stumps.
  return {
    width: isCheckpoint ? 276 : 212,
    height: isCheckpoint ? 154 : 118,
    label: {
      // Center the number over the leaf's main surface, away from its right tip.
      x: isCheckpoint ? 125 : 94,
      y: isCheckpoint ? 71 : 53,
      color: '#FFFFFF',
      r: 0,
    },
    flag: { x: 190, y: -40 },
  };
}

/** Urashima implementation of the shared Dream Drift presentation interface. */
export const dreamDriftThemeUrashima: DreamDriftThemeProvider = {
  getAssets,
  getHomePetAssets,
  getHudButtonAssets,
  getIntroImagePath,
  getPuzzleBackground,
  getMapConfig,
  getMapImagePath,
  getNodeImage,
  getNodeStyle,
};
